/// <reference types="google.maps" />
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGoogleMapsLoader } from '@/hooks/useGoogleMapsLoader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface GeoPromotionMapPickerProps {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  onLocationChange: (lat: number, lng: number) => void;
}

const DEFAULT_CENTER = { lat: 19.4326, lng: -99.1332 }; // Mexico City

const GeoPromotionMapPicker: React.FC<GeoPromotionMapPickerProps> = ({
  latitude,
  longitude,
  radiusMeters,
  onLocationChange,
}) => {
  const { t } = useTranslation();
  const { isLoaded, error } = useGoogleMapsLoader();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ description: string; place_id: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const center = latitude && longitude ? { lat: latitude, lng: longitude } : DEFAULT_CENTER;
  const hasValidLocation = latitude !== 0 && longitude !== 0;

  const updateMarker = useCallback((lat: number, lng: number) => {
    if (!mapInstance.current) return;
    const pos = new google.maps.LatLng(lat, lng);

    if (markerRef.current) {
      markerRef.current.setPosition(pos);
    } else {
      markerRef.current = new google.maps.Marker({
        position: pos,
        map: mapInstance.current,
        draggable: true,
        title: t('geoPromotions.dragToAdjust', 'Drag to adjust location'),
      });
      markerRef.current.addListener('dragend', () => {
        const p = markerRef.current!.getPosition()!;
        onLocationChange(p.lat(), p.lng());
      });
    }

    if (circleRef.current) {
      circleRef.current.setCenter(pos);
    } else {
      circleRef.current = new google.maps.Circle({
        map: mapInstance.current,
        center: pos,
        radius: radiusMeters,
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.4,
        strokeWeight: 2,
      });
    }
  }, [onLocationChange, radiusMeters, t]);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstance.current) return;

    mapInstance.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: hasValidLocation ? 15 : 5,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstance.current.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        onLocationChange(e.latLng.lat(), e.latLng.lng());
      }
    });

    if (hasValidLocation) {
      updateMarker(latitude, longitude);
    }
  }, [isLoaded]);

  // Update marker when lat/lng change from outside
  useEffect(() => {
    if (!mapInstance.current || !hasValidLocation) return;
    updateMarker(latitude, longitude);
    mapInstance.current.panTo({ lat: latitude, lng: longitude });
  }, [latitude, longitude, updateMarker, hasValidLocation]);

  // Update circle radius
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setRadius(radiusMeters);
    }
  }, [radiusMeters]);

  // Search autocomplete via existing edge function
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      const { data, error } = await supabase.functions.invoke('validate-location', {
        body: { input: value, type: 'address' },
      });
      if (!error && data?.predictions) {
        setSuggestions(data.predictions);
        setShowSuggestions(true);
      }
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion: { description: string; place_id: string }) => {
    setSearchQuery(suggestion.description);
    setShowSuggestions(false);

    // Geocode the place_id
    const { data, error } = await supabase.functions.invoke('validate-location', {
      body: { input: suggestion.place_id, type: 'geocode' },
    });

    if (!error && data?.latitude && data?.longitude) {
      onLocationChange(data.latitude, data.longitude);
      if (mapInstance.current) {
        mapInstance.current.setZoom(15);
        mapInstance.current.panTo({ lat: data.latitude, lng: data.longitude });
      }
    }
  };

  if (error) {
    return (
      <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5 text-destructive text-sm">
        {t('geoPromotions.mapError', 'Could not load map')}: {error}
      </div>
    );
  }

  if (!isLoaded) {
    return <Skeleton className="h-[300px] w-full rounded-lg" />;
  }

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Label className="flex items-center gap-1.5 mb-1.5">
          <Search className="h-3.5 w-3.5" />
          {t('geoPromotions.searchAddress', 'Search address')}
        </Label>
        <Input
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={t('geoPromotions.searchPlaceholder', 'Type an address or business name...')}
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                onMouseDown={() => handleSelectSuggestion(s)}
              >
                <MapPin className="h-3.5 w-3.5 inline mr-2 text-muted-foreground" />
                {s.description}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        className="h-[300px] w-full rounded-lg border"
        style={{ minHeight: 300 }}
      />

      {/* Read-only coordinates */}
      <div className="flex gap-3">
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">{t('geoPromotions.latitude')}</Label>
          <Input value={hasValidLocation ? latitude.toFixed(6) : ''} readOnly className="bg-muted text-xs" />
        </div>
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">{t('geoPromotions.longitude')}</Label>
          <Input value={hasValidLocation ? longitude.toFixed(6) : ''} readOnly className="bg-muted text-xs" />
        </div>
      </div>
      {!hasValidLocation && (
        <p className="text-xs text-muted-foreground">
          {t('geoPromotions.clickMapHint', 'Search for an address or click on the map to set the location.')}
        </p>
      )}
    </div>
  );
};

export default GeoPromotionMapPicker;
